import { ArrowRight } from "lucide-react";
import { C, fontStack } from "./theme.js";

/**
 * מדיניות הפרטיות ותנאי השימוש — עמודים סטטיים ב-/privacy ו-/terms.
 *
 * הטקסטים כתובים למבנה של המערכת בפועל: בעל הנכס הוא "בעל המאגר" של
 * נתוני הדיירים שלו, המפעיל מארח ומעבד; צילומי תעודות הזהות מוצפנים
 * מקצה לקצה ואינם קריאים למפעיל; העוזר וקריאת המונים עוברים דרך ספק AI.
 * כשמשנים משהו מהותי במערכת (ספק חדש, נתון חדש שנאסף) — לעדכן כאן ולהעלות
 * את הגרסה, כי ההסכמה בהרשמה נרשמת מול מספר הגרסה.
 */
export const LEGAL = {
  version: "2026-08-21",
  // שם המפעיל ופרטי קשר לפניות בנושא פרטיות. ריק = לא מוצג.
  operator: "מפעיל מערכת ניהול הנכסים",
  contactEmail: "",
  hosting: "DigitalOcean, פרנקפורט (גרמניה)",
};

const H = ({ children }) => <h2 className="disp text-lg font-bold mt-6 mb-2">{children}</h2>;
const P = ({ children }) => <p className="text-sm mb-2" style={{ lineHeight: 1.7 }}>{children}</p>;
const L = ({ items }) => (
  <ul className="text-sm mb-2 pr-5 space-y-1" style={{ listStyle: "disc", lineHeight: 1.6 }}>
    {items.map((x, i) => <li key={i}>{x}</li>)}
  </ul>
);

function Privacy() {
  return (
    <>
      <P>
        מדיניות זו מסבירה אילו נתונים נאספים במערכת ניהול הנכסים ("המערכת"), למה הם משמשים, מי רואה אותם
        ואיך שומרים עליהם. המערכת משמשת בעלי נכסים לניהול השכרת נכסיהם, ודיירים — דרך האזור האישי
        שבעל הנכס פותח להם.
      </P>

      <H>מי אחראי על הנתונים</H>
      <P>
        <b>בעל הנכס</b> שפתח חשבון הוא בעל המאגר ביחס לנתוני הדיירים, הערבים והנכסים שהוא מזין, והוא
        האחראי לקבל את הסכמת הדיירים ולעדכן אותם בזכויותיהם. <b>{LEGAL.operator}</b> ("המפעיל") מארח את
        המערכת ומעבד את הנתונים עבור בעל הנכס, לפי מדיניות זו בלבד. בעל נכס המנהל מאגר מידע עשוי להיות
        חייב ברישום או בחובות נוספות לפי חוק הגנת הפרטיות, התשמ"א-1981 ותקנותיו — האחריות לכך עליו.
      </P>

      <H>אילו נתונים נאספים</H>
      <L items={[
        "בעל נכס: שם, כתובת אימייל, סיסמה (נשמרת כ-hash בלבד ואינה ניתנת לשחזור), חתימה דיגיטלית, פרטי תשלום להצגה בחוזה (בנק/ביט), ונתוני המנוי.",
        "נכסים ודיירים (מוזנים על ידי בעל הנכס): שם הדייר ובן/בת הזוג, טלפון, מספר תעודת זהות, תאריכי חוזה, שכר דירה, ביטחונות, ערבים ופרטיהם, קריאות מונים, תשלומים וחשבונות, הערות.",
        "צילומי תעודות זהות של דיירים וערבים: מוצפנים מקצה לקצה במכשיר, כך שרק בעל הנכס (ובמקרים מסוימים הדייר) יכולים לפענח אותם. למפעיל אין את מפתח הפענוח ואין יכולת לצפות בהם.",
        "צילומי מונים, חוזים סרוקים, חתימות וקבצים שהועלו.",
        "נתוני שימוש טכניים: זמני כניסה, כתובת IP ויומן פעולות (audit) למטרות אבטחה ותמיכה. לא נעשה שימוש בעוגיות מעקב או בפרסום.",
      ]} />

      <H>למה הנתונים משמשים</H>
      <L items={[
        "ניהול השכירות: גבייה, חשבונות צריכה, חוזים, הודעות לדיירים והאזור האישי.",
        "העוזר החכם וקריאת מונים מתמונה — טקסט השאלה והתמונה נשלחים לספק בינה מלאכותית חיצוני לצורך העיבוד בלבד, ואינם משמשים אותו לאימון.",
        "שליחת הודעות מערכת (קוד כניסה, התראות מנוי) באימייל, ואם בעל הפלטפורמה הפעיל זאת — בהודעות טלפון/וואטסאפ.",
        "אבטחה, מניעת שימוש לרעה, גיבויים ותמיכה.",
      ]} />

      <H>מי רואה מה</H>
      <L items={[
        "בעל הנכס רואה את כל הנתונים של החשבון שלו בלבד. חשבונות שונים מופרדים זה מזה.",
        "דייר רואה באזור האישי רק את הנכס שלו, את תקופת השכירות שלו, את החוזים, התשלומים והקריאות שלו — ולא דיירים אחרים או קודמים.",
        "המפעיל רואה נתוני חשבון ומנוי, ויכול לגשת לנתונים מוצפנים-לא-מקצה-לקצה לצורך תמיכה ותחזוקה בלבד, ברישום ביומן.",
        "ספקים: אחסון ובסיס נתונים (" + LEGAL.hosting + "), שירות דואר אלקטרוני, ספק בינה מלאכותית, ובעת הפעלת מנוי — ספק סליקה. הם מקבלים רק את מה שנדרש לשירותם.",
        "הנתונים לא נמכרים ולא מועברים לצדדים שלישיים למטרות שיווק.",
      ]} />

      <H>אבטחת מידע</H>
      <P>
        התקשורת מוצפנת (HTTPS), סיסמאות נשמרות כ-hash (argon2), סשנים בעוגיות חתומות, גישת דיירים בקוד
        אישי שניתן להחלפה, צילומי תעודות זהות מוצפנים מקצה לקצה, גיבויים יומיים מוצפנים, ויומן פעולות.
        אף מערכת אינה חסינה לחלוטין; בעל הנכס אחראי לשמור על סיסמתו ועל קוד השחזור של ההצפנה.
      </P>

      <H>שמירה ומחיקה</H>
      <L items={[
        "הנתונים נשמרים כל עוד החשבון פעיל. בעל נכס יכול למחוק דיירים (עם או בלי ההיסטוריה), נכסים, או את החשבון כולו.",
        "גיבויים נשמרים לתקופה מוגבלת ונמחקים במחזור הגיבוי.",
        "דייר רשאי לבקש מבעל הנכס עיון, תיקון או מחיקה של נתוניו; אפשר לפנות גם למפעיל, שיעביר את הפנייה לבעל הנכס.",
      ]} />

      <H>עוגיות</H>
      <P>המערכת משתמשת בעוגיות סשן הכרחיות בלבד (כניסה של בעל הנכס ושל הדייר). אין עוגיות פרסום או מעקב.</P>

      <H>שינויים ויצירת קשר</H>
      <P>
        מדיניות זו עשויה להתעדכן; הגרסה הנוכחית: {LEGAL.version}. שינוי מהותי יוצג במערכת.
        {LEGAL.contactEmail ? <> לפניות בנושא פרטיות: <a href={`mailto:${LEGAL.contactEmail}`} style={{ color: C.teal }}>{LEGAL.contactEmail}</a>.</> : " לפניות — דרך בעל הנכס או דרך הפרטים המופיעים במערכת."}
      </P>
    </>
  );
}

function Terms() {
  return (
    <>
      <P>
        תנאים אלה חלים על השימוש במערכת ניהול הנכסים ("המערכת") המופעלת על ידי {LEGAL.operator} ("המפעיל").
        פתיחת חשבון או שימוש באזור הדייר מהווים הסכמה לתנאים ולמדיניות הפרטיות.
      </P>

      <H>השירות</H>
      <P>
        המערכת היא כלי לניהול השכרת נכסים: מעקב גבייה, חשבונות צריכה ומונים, הפקת חוזים ונספחים לחתימה
        דיגיטלית, אזור אישי לדיירים, ועוזר חכם. המערכת אינה מחליפה ייעוץ משפטי, חשבונאי או מקצועי אחר.
      </P>

      <H>חשבון, תקופת ניסיון ומנוי</H>
      <L items={[
        "חשבון נפתח עם תקופת ניסיון חינמית שאורכה נקבע על ידי המפעיל. בסיומה נדרש מנוי (חודשי או שנתי) כדי להמשיך לערוך; עד לחידוש החשבון נשאר במצב צפייה בלבד והנתונים אינם נמחקים.",
        "המחירים, אמצעי התשלום ותקופת המנוי מוצגים במערכת לפני התשלום. תשלום שבוצע אינו מוחזר בגין תקופה שכבר התחילה, אלא אם נקבע אחרת בדין.",
        "בעל החשבון אחראי לשמירת הסיסמה וקוד השחזור, ולכל פעולה שנעשית מחשבונו.",
      ]} />

      <H>אחריות בעל הנכס</H>
      <L items={[
        "להזין נתונים נכונים ולהשתמש במערכת כדין, לרבות קבלת הסכמת הדיירים והערבים לאיסוף נתוניהם ולצילום תעודת הזהות.",
        "נוסחי החוזים, הנספחים וכתבי הערבות הם תבניות כלליות. בעל הנכס אחראי לבדוק אותם, להתאימם למקרה ולהתייעץ עם עורך דין לפי הצורך. המפעיל אינו צד לחוזה השכירות.",
        "החיובים המחושבים (חשבונות צריכה לפי מונים, הצמדה, ריבית פיגורים) הם כלי עזר; האחריות לנכונותם ולדין החל עליהם היא של בעל הנכס.",
        "לא להשתמש במערכת למטרה בלתי חוקית, לפגיעה בפרטיות, להטרדה או לניסיון גישה לנתונים של חשבון אחר.",
      ]} />

      <H>האזור האישי לדיירים</H>
      <P>
        הדייר נכנס בקוד אישי שקיבל מבעל הנכס, רואה את נתוני הנכס שלו בלבד, ויכול לדווח קריאות מונים,
        לחתום על חוזה ולשלוח בקשות (למשל הארכת חוזה). הקוד אישי ואין להעבירו. חתימה דיגיטלית באזור
        האישי נשמרת עם תאריך ומזהה, ומהווה חתימה לכל דבר בין הצדדים.
      </P>

      <H>זמינות, גיבויים ואחריות</H>
      <L items={[
        "המפעיל פועל לזמינות גבוהה ומבצע גיבויים יומיים, אך אינו מתחייב לזמינות רציפה או להיעדר תקלות.",
        "המערכת ניתנת כמות שהיא (AS IS). המפעיל לא יישא באחריות לנזק עקיף, אובדן הכנסה או אובדן נתונים שנבע משימוש במערכת, ובכל מקרה אחריותו מוגבלת לסכום ששולם עבור המנוי ב-12 החודשים שקדמו לאירוע.",
        "בעל הנכס יכול לייצא ולגבות את נתוניו בכל עת מתוך המערכת.",
      ]} />

      <H>סיום</H>
      <P>
        בעל החשבון רשאי לסגור את חשבונו בכל עת. המפעיל רשאי להשעות או לסגור חשבון שמפר תנאים אלה או
        שמנויו לא חודש, לאחר הודעה סבירה. בסגירה יימחקו הנתונים בתוך זמן סביר, למעט מה שחובה לשמור לפי דין.
      </P>

      <H>שינויים ודין</H>
      <P>
        המפעיל רשאי לעדכן תנאים אלה; הגרסה הנוכחית: {LEGAL.version}. שינוי מהותי יוצג במערכת. על
        תנאים אלה יחול דין מדינת ישראל, וסמכות השיפוט נתונה לבתי המשפט המוסמכים בישראל.
      </P>
    </>
  );
}

/** עמוד משפטי מלא — /privacy או /terms. */
export default function LegalPage({ kind = "privacy", onBack }) {
  const privacy = kind === "privacy";
  return (
    <div dir="rtl" className="min-h-screen w-full" style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-xs tracking-widest mb-1" style={{ color: C.ochre }}>ניהול נכסים</div>
        <h1 className="disp text-2xl font-bold">
          {privacy ? "מדיניות פרטיות" : "תנאי שימוש"}
        </h1>
        <div className="text-[11px] mt-1 mb-2" style={{ color: C.muted }}>
          עדכון אחרון: {LEGAL.version} ·{" "}
          <a href={privacy ? "/terms" : "/privacy"} style={{ color: C.teal }}>{privacy ? "תנאי השימוש" : "מדיניות הפרטיות"}</a>
        </div>
        <div className="rounded-2xl border p-5 mt-3" style={{ background: C.card, borderColor: C.line }}>
          {privacy ? <Privacy /> : <Terms />}
        </div>
        <button onClick={onBack} className="mt-5 text-sm flex items-center gap-1" style={{ color: C.teal }}>
          <ArrowRight size={14} /> חזרה
        </button>
      </div>
    </div>
  );
}

/** שתי הלינקים, לשורת תחתית של מסכי כניסה. */
export function LegalLinks({ className = "" }) {
  return (
    <div className={`text-[11px] text-center ${className}`} style={{ color: C.muted }}>
      <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: C.muted, textDecoration: "underline" }}>מדיניות פרטיות</a>
      {" · "}
      <a href="/terms" target="_blank" rel="noreferrer" style={{ color: C.muted, textDecoration: "underline" }}>תנאי שימוש</a>
    </div>
  );
}
